import { ImmHelper } from "@base/util/Immutable";
import { ConfiguratorItemData, IVariantData3d, productTypes } from "@biz/definitions/IVariantData3d";
import { ILayerData, LayerHelper, LockState, TreePath } from "@biz/definitions/LayerDefinitions";
import { IGroupData } from "@biz/definitions/ProductDefinitions";
import { VariantData3d } from "@biz/definitions/VariantData3d";
import { Dev } from "@biz/Dev";

export class GroupHelper {
  // concenience ... could be optimized
  static calcMergedLockState(s: IVariantData3d, sunSailId: string): LockState | undefined {
    return GroupHelper.calcMergedLayerState(s, sunSailId, Dev.isTreeNew()).locked
  }
  // concenience ... could be optimized
  static calcMergedHidden(s: IVariantData3d, sunSailId: string): boolean {
    return !!GroupHelper.calcMergedLayerState(s, sunSailId, Dev.isTreeNew()).hidden
  }

  static isOrderableItemType(itemType: ConfiguratorItemData["itemType"]): boolean {
    return (productTypes as string[]).includes(itemType)
  }
  static isOrderable(data: IVariantData3d, item: ConfiguratorItemData): boolean {
    if (!GroupHelper.isOrderableItemType(item.itemType))
      return false;
    return !GroupHelper.calcMergedLayerStateForItem(data, item, !!data.groups?.byId?.length).notOrderable;
    //if (data.groups) {
    // return !GroupHelper.calcMergedLayerStateForItem(data, item, !!data.groups).notOrderable
    // }
    // return true;
  }

  static calcLayerState(ld: ILayerData | undefined): ILayerData {
    if (ld)
      return { hidden: ld.hidden, locked: ld.locked, notOrderable: ld.notOrderable }
    return {};
  }

  static toggleLock(a: LockState | undefined): LockState {
    switch (a) {
      case false:
      case undefined:
        return "lockedTransform";
      case "lockedTransform":
        return "locked";
      case "locked":
        return false;
    }
    return false;
  }

  static mergeLockState(a: LockState | undefined, b: LockState | undefined): LockState {
    if (a === "locked" || b === "locked")
      return "locked";
    if (a === "lockedTransform" || b === "lockedTransform")
      return "lockedTransform";
    if (!!a || !!b) // old lockstate
      return "locked";
    return false;
  }

  static mergeLayerData(item: ILayerData | undefined, group: ILayerData | undefined): ILayerData {
    return {
      hidden: !!item?.hidden || !!group?.hidden,
      locked: GroupHelper.mergeLockState(item?.locked, group?.locked),
      notOrderable: !!item?.notOrderable || !!group?.notOrderable,
    }
  }

  static mergeLayerStack(stack: ILayerData[]): ILayerData {
    if (!stack)
      return {};
    let ret: ILayerData = stack[0];
    for (let i = 1; i < stack.length; i++)
      ret = GroupHelper.mergeLayerData(ret, stack[i])
    return ret;
  }

  static calcItemStack(s: IVariantData3d, id: string): ConfiguratorItemData[] {
    const stack: ConfiguratorItemData[] = [];
    let current = VariantData3d.findVariantData3dItem(s, id);
    while (current) {
      stack.unshift(current);
      current = current.parentId ? VariantData3d.findVariantData3dItem(s, current.parentId) : undefined;
    }
    return stack;
  }

  static calcLayerStateStack(s: IVariantData3d, id: string, useTreeNew: boolean): ILayerData[] {
    if (!useTreeNew) {
      const itemStack = GroupHelper.calcItemStack(s, id);
      if (itemStack.length === 0)
        return [];
      const layerStack = LayerHelper._getLayerStackForSelectionType(itemStack[itemStack.length - 1].itemType);
      const stack = layerStack.map((stackId) => s.layers[stackId] || {});
      return [...stack, ...itemStack];
    }

    return GroupHelper.calcItemStack(s, id);
  }

  static calcMergedLayerState(s: IVariantData3d, id: string, useTreeNew: boolean): ILayerData {
    const stack = GroupHelper.calcLayerStateStack(s, id, useTreeNew);
    if (!stack?.length)
      return GroupHelper.notFoundLayerData;

    return GroupHelper.mergeLayerStack(stack);
  }

  static notFoundLayerData: ILayerData = { hidden: true, locked: "locked", notOrderable: true }

  /** @deprecated */
  static calcMergedLayerStateOld(layers: Record<string, ILayerData>, layerStack: TreePath): ILayerData {
    let layerData: ILayerData = {};
    if (layerStack) {
      for (const p of layerStack) {
        const layer = layers[p];
        if (layer) {
          layerData = GroupHelper.mergeLayerData(layerData, layer);
        }
      }
    }
    return layerData;
  }

  static calcMergedLayerStateForItem(s: IVariantData3d, item: ConfiguratorItemData, useTreeNew: boolean): ILayerData {
    if (!item)
      return {};

    const parentLayerState = GroupHelper.calcMergedParentLayerStateForItem(s, item, useTreeNew);
    return GroupHelper.mergeLayerData(item, parentLayerState);
  }

  static calcMergedParentLayerStateForItem(s: IVariantData3d, item: ConfiguratorItemData, useTreeNew: boolean): ILayerData {
    if (!item)
      return {};

    if (!useTreeNew)
      return GroupHelper.calcMergedLayerStateOld(s.layers, LayerHelper._getLayerStackForSelectionType(item.itemType))

    if (item.parentId) {
      const parentStack = GroupHelper.calcItemStack(s, item.parentId);
      return GroupHelper.mergeLayerStack(parentStack);
    }
    return {}
  }

  static setGroupIdForItem(old: IVariantData3d, itemId: string, groupId: string | undefined): IVariantData3d {
    return VariantData3d.updateConfiguratorItem(old, itemId, (child) => ImmHelper.setProperty(child, "parentId", groupId));
  }

  static setGroupIdForItems(old: IVariantData3d, itemIds: string[], groupId: string | undefined): IVariantData3d {
    for (const itemId of itemIds) {
      old = GroupHelper.setGroupIdForItem(old, itemId, groupId);
    }
    return old;
  }

  static addToChildList(old: IVariantData3d, groupId: string, itemId: string): IVariantData3d {
    return VariantData3d.updateConfiguratorItem(old, groupId, (group) => {
      if (group?.itemType !== "group" || group.children.includes(itemId))
        return group;
      const newGroup: IGroupData = {
        ...group,
        children: [...group.children, itemId]
      }
      return newGroup;
    });
  }

  static removeFromChildList(old: IVariantData3d, groupId: string, itemId: string | string[]): IVariantData3d {
    return VariantData3d.updateConfiguratorItem(old, groupId, (groupItem) => {
      if (groupItem?.itemType !== "group")
        return groupItem;
      const newGroup: IGroupData = {
        ...groupItem,
        children: Array.isArray(itemId) ? groupItem.children.filter((i) => !itemId.includes(i)) : groupItem.children.filter((i) => i !== itemId)
      }
      return newGroup;
    })
  }

  static setChildList(old: IVariantData3d, groupId: string, list: string[]): IVariantData3d {
    return VariantData3d.updateConfiguratorItem(old, groupId, (groupItem) => {
      if (groupItem?.itemType !== "group")
        return groupItem;
      const newGroup: IGroupData = {
        ...groupItem,
        children: list
      }
      return newGroup;
    });
  }

  static getGroup(old: IVariantData3d, idOrIds: string | string[]): { id: string | undefined, same: boolean } {
    const ids = Array.isArray(idOrIds) ? idOrIds : [idOrIds];
    if (ids.length === 0)
      return { id: undefined, same: true };
    const groups = ids.map((id) => VariantData3d.findVariantData3dItem(old, id)?.parentId);
    if (ids.length === 1)
      return { id: groups[0], same: true };
    return { id: groups[0], same: groups.every((item) => item === groups[0]) }
  }

  static calcRecursiveChildIds(old: IVariantData3d, idOrIds: string | string[]): string[] {
    const ids = Array.isArray(idOrIds) ? [...idOrIds] : [idOrIds];
    const childIds: string[] = [];
    let index = 0;
    while (index < ids.length) {
      const item = VariantData3d.findVariantData3dItem(old, ids[index]);
      if (item?.itemType === "group") {
        if (item.children?.length)
          ids.push(...item.children);
      } else {
        childIds.push(ids[index]);
      }
      index++;
    }
    return childIds;
  }
}